/**
 * geolocation.ts
 * @version 1.0.0
 *
 * Hook React pour le suivi GPS en temps réel + reverse geocoding via Nominatim.
 * Aucune dépendance externe. Fallback robuste si GPS refusé ou offline.
 */

import { useState, useEffect, useRef, useCallback } from 'react'

// ── Types ────────────────────────────────────────────────────────────────────

export interface GeoPosition {
  lat: number
  lng: number
  accuracy: number
  timestamp: number
}

export interface GeoState {
  position: GeoPosition | null
  error: string | null
  /** Adresse lisible obtenue par reverse geocoding. null = pas encore résolu. */
  address: string | null
}

// ── Reverse Geocoding (Nominatim — OpenStreetMap) ────────────────────────────

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/reverse'
const GEOCODE_TIMEOUT_MS = 3_000

/**
 * Transforme des coordonnées GPS en adresse lisible.
 * Timeout 3s. Retourne null en cas d'échec (offline, erreur réseau, etc.).
 */
export async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), GEOCODE_TIMEOUT_MS)

  try {
    const url = `${NOMINATIM_URL}?lat=${lat}&lon=${lng}&format=json&addressdetails=1&zoom=18&accept-language=fr`
    const res = await fetch(url, {
      signal: controller.signal,
      headers: { 'User-Agent': 'RentalCheck-OlympicLocation/1.0' },
    })

    if (!res.ok) return null

    const data = await res.json()
    if (!data?.address) return null

    // Construire une adresse lisible et concise
    const a = data.address
    const parts: string[] = []

    // Numéro + rue
    const street = a.road || a.pedestrian || a.footway || ''
    if (a.house_number && street) {
      parts.push(`${a.house_number} ${street}`)
    } else if (street) {
      parts.push(street)
    }

    // Ville
    const city = a.city || a.town || a.village || a.municipality || ''
    if (city) {
      const postcode = a.postcode || ''
      parts.push(postcode ? `${city} ${postcode}` : city)
    }

    return parts.length > 0 ? parts.join(', ') : (data.display_name?.split(',').slice(0, 3).join(',') || null)
  } catch {
    // Timeout, offline, ou erreur réseau → fallback silencieux
    return null
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Formate les coordonnées GPS en texte lisible (fallback si reverse geocoding échoue).
 */
export function formatCoords(lat: number, lng: number): string {
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`
}

// ── Hook React ─────────────────────────────────────────────────────────────

/**
 * useGeolocation()
 *
 * Maintient la position GPS la plus fraîche via watchPosition.
 * Ne bloque jamais le flux utilisateur.
 *
 * Retourne : { position, error, address }
 */
export function useGeolocation(): GeoState {
  const [state, setState] = useState<GeoState>({
    position: null,
    error: null,
    address: null,
  })

  const watchIdRef = useRef<number | null>(null)
  const lastGeocodeRef = useRef<{ lat: number; lng: number } | null>(null)

  // Résoudre l'adresse en arrière plan (debounced via seuil de distance)
  const resolveAddress = useCallback(async (lat: number, lng: number) => {
    // Ne pas re-geocoder si on est trop proches de la dernière position
    if (lastGeocodeRef.current) {
      const dLat = Math.abs(lat - lastGeocodeRef.current.lat)
      const dLng = Math.abs(lng - lastGeocodeRef.current.lng)
      // ~10m de tolérance → éviter spam API
      if (dLat < 0.0001 && dLng < 0.0001) return
    }

    lastGeocodeRef.current = { lat, lng }
    const address = await reverseGeocode(lat, lng)
    if (address) {
      setState(prev => ({ ...prev, address }))
    }
  }, [])

  useEffect(() => {
    if (!navigator.geolocation) {
      setState(prev => ({ ...prev, error: 'Géolocalisation non supportée' }))
      return
    }

    const onSuccess = (pos: GeolocationPosition) => {
      const newPosition: GeoPosition = {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        timestamp: pos.timestamp,
      }
      setState(prev => ({ ...prev, position: newPosition, error: null }))
      resolveAddress(newPosition.lat, newPosition.lng)
    }

    const onError = (err: GeolocationPositionError) => {
      let errorMsg: string
      switch (err.code) {
        case err.PERMISSION_DENIED:
          errorMsg = 'Accès GPS refusé'
          break
        case err.POSITION_UNAVAILABLE:
          errorMsg = 'Position GPS indisponible'
          break
        case err.TIMEOUT:
          errorMsg = 'Délai GPS dépassé'
          break
        default:
          errorMsg = 'Erreur GPS inconnue'
      }
      setState(prev => ({ ...prev, error: errorMsg }))
    }

    watchIdRef.current = navigator.geolocation.watchPosition(onSuccess, onError, {
      enableHighAccuracy: true,
      timeout: 10_000,
      maximumAge: 30_000, // Accepter une position de 30s pour éviter l'attente
    })

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current)
      }
    }
  }, [resolveAddress])

  return state
}
